import { createTheme } from '@mui/material/styles';

/**
 * App-wide MUI theme — the single source for the SM4RT accent + control styling
 * (see the `sm4rt-web-ui` skill). Setting the accent as `palette.primary`
 * themes every primary-coloured MUI control (buttons, checkboxes, radios,
 * switches, links, input focus) across the whole site in one place; the
 * component overrides align outlined inputs to the design-system border/radius.
 */
const ACCENT = '#4A90D9';
const ACCENT_HOVER = '#3B82C4';

/**
 * Toast / feedback tones (see the `sm4rt-web-ui` skill §Toasts). Every MUI
 * `Alert` — whether floating in a `Snackbar` or inline in a page body — renders
 * in this quiet, light "card" style: a tinted background + hairline border, a
 * coloured status icon, and dark readable text (never solid/white-on-colour).
 * These are the shared Chip tones so a success toast and a success chip agree.
 * `variant="filled"` is deprecated for alerts — it's mapped to the same look
 * here so legacy call sites can't produce a jarring solid banner.
 */
const ALERT_TONES = {
  Success: { bg: '#e8f6f0', border: '#b6e2d3', icon: '#1D9E75' },
  Error: { bg: '#fdecea', border: '#f5c6c0', icon: '#b03a2e' },
  Warning: { bg: '#fffbf0', border: '#fac775', icon: '#c07a10' },
  Info: { bg: '#e6f1fb', border: '#b5d4f4', icon: ACCENT },
};

// Build standard*/filled* severity overrides from the shared tones so both
// variants render identically (filled is neutralised to the light style).
const alertSeverityOverrides = Object.entries(ALERT_TONES).reduce(
  (acc, [severity, tone]) => {
    const style = {
      backgroundColor: tone.bg,
      color: '#1a1a1a',
      borderColor: tone.border,
      '& .MuiAlert-icon': { color: tone.icon },
    };
    acc[`standard${severity}`] = style;
    acc[`filled${severity}`] = style;
    return acc;
  },
  {} as Record<string, object>,
);

export const theme = createTheme({
  palette: {
    primary: { main: ACCENT, dark: ACCENT_HOVER, contrastText: '#ffffff' },
  },
  components: {
    MuiButton: {
      // Sentence-case (MUI defaults to UPPERCASE) + flat, matching the dashboard.
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none' },
      },
    },
    MuiAlert: {
      // One light, on-brand style for every toast/feedback banner app-wide
      // (see the `sm4rt-web-ui` skill §Toasts). Design-system card shape:
      // 8px radius, hairline ring, subtle float shadow, 13px medium dark text,
      // a coloured status icon, and a recessive grey close affordance.
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '0.5px solid transparent',
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 500,
          color: '#1a1a1a',
          alignItems: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.04)',
        },
        icon: { padding: 0, marginRight: 10, opacity: 1 },
        message: { padding: '2px 0', color: '#1a1a1a' },
        action: {
          paddingTop: 0,
          marginRight: -4,
          color: '#888888',
          '& .MuiButtonBase-root:hover': { color: '#555555', backgroundColor: 'transparent' },
        },
        ...alertSeverityOverrides,
      },
    },
    MuiOutlinedInput: {
      // Design-system input: hairline #d0dcea border, 6px radius, and the quiet
      // dashboard focus — a plain accent border-colour change (thin 1px border,
      // not MUI's default 2px), no glow ring.
      styleOverrides: {
        root: {
          borderRadius: 6,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0dcea' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c5d9f0' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ACCENT,
            borderWidth: 1,
          },
        },
      },
    },
    // ── Control value text: 14px everywhere ──────────────────────────────────
    // MUI sizes every input, menu item and Autocomplete option from `body1`
    // (1rem/16px). The design system's comfortable tier is 14px, so surfaces
    // used to redeclare it one at a time (`FIELD_SX`, page CSS, per-component
    // `sx`) — which left every surface that forgot at 16px, and could never fix
    // the portal-rendered ones at all: Select menus and Autocomplete listboxes
    // mount at `document.body`, out of reach of any page-scoped selector. Hence
    // the one declaration here.
    MuiInputBase: {
      // The `input` slot (not `root`) so it lands on the element itself — Select
      // renders its value div with this class too.
      styleOverrides: {
        input: { fontSize: 14 },
      },
    },
    MuiInputLabel: {
      // The floating label matches the value text it sits on top of. While a
      // field is empty and unfocused MUI shows the *label* in the input (the
      // real placeholder stays hidden until the label shrinks), so a 16px label
      // over 14px value text reads as an oversized placeholder that "shrinks"
      // the moment you click in.
      //
      // Only the resting size moves. The shrunk label is a `scale(0.75)`
      // transform, and the notched-outline legend it sits in is `0.75em` of the
      // *root* font size (still 1rem) — so the notch stays slightly wider than
      // the shrunk text rather than clipping it. Pages wanting a tighter notch
      // scope their own shrink size (see `TicketEntryPage.css`).
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiMenuItem: {
      // Covers Select dropdowns and menus. Deliberately does NOT cascade into
      // nested Typography — menu rows that use `variant="caption"`/`"overline"`
      // for a secondary line are meant to stay smaller.
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiAutocomplete: {
      // Plain-string options + the empty/loading rows. Options built from custom
      // markup set their own sizes and are left alone (same reasoning as above).
      styleOverrides: {
        option: { fontSize: 14 },
        noOptions: { fontSize: 14 },
        loading: { fontSize: 14 },
      },
    },
    MuiInputAdornment: {
      // MUI wraps a *string* adornment (e.g. the `$` prefix on price fields) in
      // a bare `Typography` — body1 again, so it outsized the 14px value beside
      // it. No adornment in this app passes its own Typography, so scoping to
      // the wrapper class is safe.
      styleOverrides: {
        root: { '& .MuiTypography-root': { fontSize: 14 } },
      },
    },
  },
});
